import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetPressReleaseDetail, getServerPageGetPressReleaseSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { BodyTextFieldsFragment, PageInformationFieldsFragment, PressReleaseFieldsFragment } from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { ModuleBuilder } from 'modules/module-builder/module-builder';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IPressReleaseDetailPageProps extends IBasePageProps {
	page?: PressReleaseFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any; // could add fragment type if needed
}

const PressReleaseDetailPage = (props: IPressReleaseDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.page || null,
		pageQuery: () => getServerPageGetPressReleaseDetail({ variables: props.variables }, client)
	});

	const modules = props?.page?.modules || [];
	const seoModule = page?.seo;
	const slug = page?.slug;

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'PressRelease' ? (
				<>
					<IntroHeader module={page.header} slug={slug} />
					{page.bodyText ? (
						<BaseModule defaultModuleOptions={page.bodyText?.defaultModuleOptions}>
							<BodyText module={page.bodyText as BodyTextFieldsFragment} publishedAt={page.firstPublishedAt} />
						</BaseModule>
					) : (
						<></>
					)}
					<ModuleBuilder modules={modules} slug={slug || ''} />
				</>
			) : (
				<PasswordLayout locale={props.variables?.locale} slug={props.variables?.slug}>
					<PasswordModule />
				</PasswordLayout>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetPressReleaseSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.PRESS_RELEASES.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.PRESS_RELEASES.slug}/${ctx.params.slug}` : '/';
		const variables = {
			slug,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<
			Awaited<ReturnType<typeof getServerPageGetPressReleaseDetail>>,
			PressReleaseFieldsFragment,
			PageInformationFieldsFragment
		>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::press-release.press-release',
			contentTypeName: 'press-release',
			fetchPage: () => getServerPageGetPressReleaseDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const pageNode = data?.props?.data?.pressReleaseBySlug?.data;
				const pageModules = pageNode ? pageNode.attributes.modules || [] : [];
				const detailNode = data?.props?.data?.pressReleaseDetail?.data;
				const detailModules = detailNode ? detailNode?.attributes?.modules || [] : [];

				return pageNode
					? {
							documentId: pageNode.documentId,
							attributes: { ...pageNode.attributes, modules: [...pageModules, ...detailModules] }
					  }
					: null;
			},
			neutralStyle: false,
			isSubsite: false
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: null,
				variables,
				...passwordResult
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in press-release detail', error);

		return {
			notFound: true
		};
	}
};

export default PressReleaseDetailPage;
